import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import WagerPanel from "@/components/WagerPanel";
import { toast } from "sonner";
import { maybeCelebrate } from "@/lib/celebrate";
import { playSfx, startAmbient, stopAmbient } from "@/lib/sfx";
import { Sparkles, RotateCcw, ShieldCheck, Wind } from "lucide-react";

const MAX_MULT = 10.0;

/**
 * Magic Monday — a horse transforms into a unicorn on lift-off.
 * The unicorn flies (multiplier grows). Storm clouds occasionally pass through
 * and shrink the multiplier when they hit. The unicorn reverts to a horse at a
 * server-decided random time — cash out before that happens.
 * All fairness is server-authoritative; the client just animates.
 */
export default function Unicorn() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [weekendBoost, setWeekendBoost] = useState(false);
  const [boostGrowthPct, setBoostGrowthPct] = useState(25);
  const [boostHazardPct, setBoostHazardPct] = useState(15);
  const [difficulty, setDifficulty] = useState("swift");
  const [clientSeed, setClientSeed] = useState("");
  const [session, setSession] = useState(null);
  const [seedHash, setSeedHash] = useState("");
  const [seedReveal, setSeedReveal] = useState("");
  const [flying, setFlying] = useState(false);
  const [mult, setMult] = useState(1.0);
  const [elapsed, setElapsed] = useState(0);
  const [cloudHits, setCloudHits] = useState(0);
  const [shake, setShake] = useState(false);
  const [reverted, setReverted] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [visibleClouds, setVisibleClouds] = useState([]);
  const startTs = useRef(null);
  const rafId = useRef(null);
  const cloudSpawnTimers = useRef([]);
  const revertTimeout = useRef(null);
  const settleGuard = useRef(false);

  useEffect(() => {
    api.get("/games/unicorn/config").then((r) => {
      setConfigs(r.data.difficulties);
      setWeekendBoost(!!r.data.weekend_boost);
      setBoostGrowthPct(r.data.boost_growth_pct ?? 25);
      setBoostHazardPct(r.data.boost_hazard_pct ?? 15);
    });
    setClientSeed(Math.random().toString(36).slice(2, 12).toUpperCase());
  }, []);

  const cfg = configs.find((c) => c.id === difficulty);
  const potential = session ? Math.floor(session.wager * mult) : 0;

  // Local mult growth animation — uses the exact same math as the server so the
  // cashout multiplier displayed equals the payout multiplier.
  const tick = useCallback(() => {
    if (!startTs.current || !session) return;
    const now = performance.now();
    const eSec = (now - startTs.current) / 1000;
    setElapsed(eSec);
    const growth = session.growth_per_s ?? cfg?.growth_per_s ?? 0;
    const shrink = session.cloud_shrink ?? cfg?.cloud_shrink ?? 0;
    const schedule = session.clouds || [];
    let m = growth * eSec;
    // apply the same cloud shrinks the server will apply at settlement
    let hits = 0;
    for (const t of schedule) {
      if (t <= eSec) { m *= (1 - shrink); hits += 1; } else break;
    }
    if (hits !== cloudHits) setCloudHits(hits);
    setMult(Math.min(session.max_mult ?? 10, Math.max(0, m)));
    rafId.current = requestAnimationFrame(tick);
  }, [cfg, session, cloudHits]);

  const cleanupFlight = () => {
    if (rafId.current) cancelAnimationFrame(rafId.current);
    rafId.current = null;
    startTs.current = null;
    cloudSpawnTimers.current.forEach((t) => clearTimeout(t));
    cloudSpawnTimers.current = [];
    if (revertTimeout.current) { clearTimeout(revertTimeout.current); revertTimeout.current = null; }
    stopAmbient();
  };

  // Spawn visual clouds at the EXACT times the server schedule dictates, so
  // the multiplier drop the user sees always matches the server's payout math.
  const scheduleClouds = (schedule) => {
    (schedule || []).forEach((t, idx) => {
      // spawn the cloud 1.2 seconds BEFORE it hits so animation reaches unicorn exactly at t
      const spawnAt = Math.max(0, t * 1000 - 1200);
      const yPct = 25 + ((idx * 37) % 50);
      const cloudId = `c${idx}`;
      const t1 = setTimeout(() => {
        setVisibleClouds((cs) => [...cs, { id: cloudId, yPct, ts: performance.now() }]);
        // hit exactly 1200ms later — matches the shrink applied in tick()
        const t2 = setTimeout(() => {
          setShake(true);
          playSfx("splash");
          setTimeout(() => setShake(false), 320);
        }, 1200);
        cloudSpawnTimers.current.push(t2);
        // remove cloud after animation completes
        const t3 = setTimeout(() => {
          setVisibleClouds((cs) => cs.filter((c) => c.id !== cloudId));
        }, 3400);
        cloudSpawnTimers.current.push(t3);
      }, spawnAt);
      cloudSpawnTimers.current.push(t1);
    });
  };

  const start = async (wager) => {
    setBusy(true);
    try {
      const { data } = await api.post("/games/wager", {
        game: "unicorn", wager,
        config: { difficulty, client_seed: clientSeed },
      });
      settleGuard.current = false;
      setSession(data);
      setSeedHash(data.server_seed_hash || "");
      setSeedReveal("");
      setMult(1.0); setElapsed(0); setCloudHits(0); setVisibleClouds([]);
      setReverted(false); setResult(null); setFlying(true);
      await refresh();

      playSfx("wagerConfirm");
      // Transformation SFX — a sparkly rise
      setTimeout(() => playSfx("match"), 240);
      setTimeout(() => playSfx("match"), 420);
      startAmbient("unicorn");
      startTs.current = performance.now();
      rafId.current = requestAnimationFrame(tick);
      scheduleClouds(data.clouds);
      // Safety timeout: hard-cap flight at 30s (server also caps)
      revertTimeout.current = setTimeout(() => {
        if (!settleGuard.current) autoRevert();
      }, 30000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cannot start");
    } finally {
      setBusy(false);
    }
  };

  const cashOut = async () => {
    if (!flying || busy || settleGuard.current) return;
    settleGuard.current = true;
    setBusy(true);
    const elapsedMs = Math.round((performance.now() - (startTs.current || performance.now())));
    cleanupFlight();
    setFlying(false);
    try {
      const { data: cash } = await api.post("/games/unicorn/cashout", {
        session_id: session.session_id, elapsed_ms: elapsedMs,
      });
      if (cash.crashed) {
        // Server says we were too late — reverted before cashout
        setReverted(true);
        playSfx("lose");
        setTimeout(() => finalize(cash), 700);
      } else {
        setMult(cash.verified_mult);
        playSfx("cashOut");
        setTimeout(() => finalize(cash), 300);
      }
    } catch (e) {
      settleGuard.current = false; setBusy(false);
      toast.error(e.response?.data?.detail || "Cash-out failed");
    }
  };

  const autoRevert = async () => {
    if (settleGuard.current) return;
    settleGuard.current = true;
    setBusy(true);
    const elapsedMs = Math.round((performance.now() - (startTs.current || performance.now())));
    cleanupFlight();
    setFlying(false);
    setReverted(true);
    playSfx("lose");
    try {
      const { data: cash } = await api.post("/games/unicorn/cashout", {
        session_id: session.session_id, elapsed_ms: elapsedMs + 500,
      });
      setTimeout(() => finalize(cash), 700);
    } catch (e) {
      settleGuard.current = false; setBusy(false);
      toast.error(e.response?.data?.detail || "Round failed");
    }
  };

  const finalize = async (cash) => {
    try {
      const { data } = await api.post("/games/settle", {
        session_id: session.session_id,
        outcome: { cashed_out: !cash.crashed, hit: cash.crashed },
      });
      setResult(data);
      setSeedReveal(data.server_seed_reveal || "");
      const netWin = data.payout > session.wager;
      const netLoss = data.payout <= session.wager;
      if (netWin) {
        toast.success(`+${(data.payout - session.wager).toLocaleString()} chips`);
        maybeCelebrate({ payout: data.payout, wager: session.wager });
        if (data.payout >= session.wager * 3) playSfx("megaWin");
      } else if (data.payout > 0) {
        toast.warning(`Partial cashout · net ${data.payout - session.wager} chips`);
      } else {
        toast.error(`Reverted · lost ${session.wager} chips`);
      }
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Settle failed");
    } finally { setBusy(false); }
  };

  const restart = () => {
    cleanupFlight();
    setSession(null); setResult(null); setSeedHash(""); setSeedReveal("");
    setMult(1.0); setElapsed(0); setCloudHits(0); setVisibleClouds([]);
    setReverted(false); setFlying(false);
    setClientSeed(Math.random().toString(36).slice(2, 12).toUpperCase());
  };

  const copyProof = () => {
    navigator.clipboard.writeText(JSON.stringify({
      server_seed_hash: seedHash, server_seed: seedReveal,
      client_seed: clientSeed, schedule: result?.schedule, elapsed_s: result?.elapsed_s,
    }, null, 2));
    toast.success("Round proof copied");
  };

  useEffect(() => () => cleanupFlight(), []);

  return (
    <div className="space-y-6" data-testid="unicorn-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="pill pill-gold inline-flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Magic Monday
          </div>
          <h1 className="font-display text-3xl mt-2">Ride the unicorn · cash out before the spell breaks</h1>
        </div>
        <button onClick={() => nav("/lobby")} className="btn-ghost text-sm" data-testid="back-to-lobby">Lobby</button>
      </div>

      {weekendBoost && (
        <div className="rounded-2xl overflow-hidden relative border border-amber-400/40" data-testid="weekend-boost-banner" style={{
          background: "linear-gradient(90deg, rgba(244,165,192,0.15) 0%, rgba(251,191,36,0.18) 50%, rgba(217,196,232,0.15) 100%)",
        }}>
          <div className="absolute inset-0 pointer-events-none opacity-40" style={{
            background: "radial-gradient(circle at 20% 50%, rgba(251,191,36,0.35), transparent 40%), radial-gradient(circle at 80% 50%, rgba(244,165,192,0.35), transparent 40%)",
          }} />
          <div className="relative flex items-center gap-4 p-4">
            <div className="text-3xl leading-none">✨</div>
            <div className="flex-1 min-w-0">
              <div className="pill pill-gold inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> Weekend Streak Active</div>
              <div className="mt-1.5 text-sm text-slate-100">
                <span className="font-mono text-[color:var(--gold)]">+{boostGrowthPct}%</span> faster multiplier growth ·
                <span className="font-mono text-[color:var(--gold)] ml-1">−{boostHazardPct}%</span> slower revert · every Fri, Sat &amp; Sun
              </div>
            </div>
          </div>
        </div>
      )}

      {!session ? (
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="vexis-card p-5">
            <div className="pill">Flight mode</div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {configs.map((c) => (
                <button key={c.id} onClick={() => setDifficulty(c.id)} data-testid={`unicorn-diff-${c.id}`}
                  className={`text-left p-3 rounded-xl border transition ${difficulty === c.id ? "border-[color:var(--gold)] text-[color:var(--gold)] bg-[rgba(226,185,106,0.06)]" : "border-white/10 text-slate-300 hover:border-white/20"}`}>
                  <div className="text-sm capitalize">{c.id}</div>
                  <div className="text-xs font-mono text-slate-500 mt-1">
                    +{c.growth_per_s.toFixed(2)}×/s · revert {(1 / c.hazard_per_s).toFixed(1)}s avg · storms {(c.cloud_per_s * 60).toFixed(0)}/min
                  </div>
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-3">Storm clouds shrink your multiplier by {cfg ? Math.round(cfg.cloud_shrink * 100) : 12}% each hit. Max payout {MAX_MULT}×.</p>
          </div>
          <WagerPanel game="Magic Monday" onStart={start} busy={busy} />
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
          <div className={`vexis-card p-4 sm:p-6 select-none relative overflow-hidden ${result ? (result.payout > session.wager ? "win-glow" : "loss-glow") : ""}`}>
            <MagicMondayScene
              flying={flying}
              reverted={reverted}
              mult={mult}
              shake={shake}
              visibleClouds={visibleClouds}
              cloudHits={cloudHits}
              elapsed={elapsed}
              boostActive={!!session?.weekend_boost}
            />
          </div>

          <div className="space-y-3">
            <div className="vexis-card p-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-slate-500 text-xs font-mono uppercase">Multiplier</div><div className="font-display text-xl text-[color:var(--gold)]" data-testid="unicorn-mult">{mult.toFixed(2)}×</div></div>
                <div><div className="text-slate-500 text-xs font-mono uppercase">Potential</div><div className="font-display text-xl" data-testid="unicorn-potential">{potential.toLocaleString()}</div></div>
                <div><div className="text-slate-500 text-xs font-mono uppercase">Flight</div><div className="font-display text-xl">{elapsed.toFixed(1)}s</div></div>
                <div><div className="text-slate-500 text-xs font-mono uppercase">Wager</div><div className="font-display text-xl">{session.wager}</div></div>
              </div>
              {cloudHits > 0 && (
                <div className="mt-3 pt-3 border-t border-white/5 text-xs text-amber-300 inline-flex items-center gap-1.5">
                  <Wind className="w-3.5 h-3.5" /> {cloudHits} storm hit{cloudHits === 1 ? "" : "s"} · mult shrunk
                </div>
              )}
            </div>

            {!result && flying && (
              <button onClick={cashOut} disabled={busy} className="btn-gold w-full inline-flex justify-center items-center gap-2" data-testid="unicorn-cashout">
                Cash out · {potential.toLocaleString()} chips
              </button>
            )}

            {result && (
              <div className={`vexis-card p-4 ${result.payout > session.wager ? "win-glow" : "loss-glow"}`} data-testid="unicorn-result">
                {result.payout >= session.wager * 2 && (
                  <div className="pill pill-gold mb-2 inline-flex" data-testid="unicorn-mega">✦ Mega Win · {(result.payout / session.wager).toFixed(2)}×</div>
                )}
                <div className={`font-display text-2xl ${result.payout > session.wager ? "text-emerald-400" : (result.payout > 0 ? "text-amber-400" : "text-red-400")}`}>
                  {result.payout > session.wager ? "Cashed out · profit" : (result.payout > 0 ? "Cashed out · partial" : "Reverted to horse")}
                </div>
                <div className="text-sm mt-1 text-slate-300">
                  Payout · <span className="font-mono">{result.payout}</span> chips · Net <span className={`font-mono ${result.payout > session.wager ? "text-emerald-400" : "text-red-400"}`}>{result.payout - session.wager >= 0 ? "+" : ""}{result.payout - session.wager}</span>
                </div>
                {result.schedule?.crash_s && (
                  <div className="text-xs text-slate-500 mt-2 font-mono">Revert scheduled at {result.schedule.crash_s.toFixed(2)}s · {result.schedule.clouds?.length || 0} storms</div>
                )}
                <div className="mt-3 flex items-center gap-2 text-xs">
                  <button onClick={copyProof} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="unicorn-verify">
                    <ShieldCheck className="w-3 h-3" /> Copy proof
                  </button>
                </div>
                <button onClick={restart} className="btn-gold w-full mt-4 inline-flex justify-center items-center gap-2" data-testid="unicorn-play-again"><RotateCcw className="w-4 h-4" /> Fly again</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Scene + Watercolor Unicorn ---------------- */

function MagicMondayScene({ flying, reverted, mult, shake, visibleClouds, cloudHits, elapsed, boostActive }) {
  // Unicorn Y position derived from multiplier (higher mult = higher altitude)
  const altitude = Math.min(60, mult * 6);
  return (
    <div className="relative rounded-2xl overflow-hidden" style={{
      minHeight: 320,
      background: "linear-gradient(180deg, #9DB8AB 0%, #B5CDBF 35%, #D4DDD1 65%, #E8DDC8 100%)",
    }}>
      {boostActive && flying && (
        <div className="absolute top-3 right-3 z-10 pill pill-gold inline-flex items-center gap-1 backdrop-blur-sm" style={{
          animation: "boostPulse 2s ease-in-out infinite",
          background: "rgba(226,185,106,0.85)",
          color: "#0B0D10",
        }} data-testid="weekend-boost-live">
          <Sparkles className="w-3 h-3" /> Weekend Boost
        </div>
      )}      {/* watercolor paper grain */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.15), transparent 40%), radial-gradient(circle at 80% 70%, rgba(0,0,0,0.05), transparent 45%)",
        mixBlendMode: "overlay",
      }} />
      {/* sun disk */}
      <div className="absolute pointer-events-none" style={{
        top: "10%", left: "12%",
        width: 60, height: 60, borderRadius: "50%",
        background: "radial-gradient(circle at 40% 40%, #FEF3C7 0%, #FBBF24 55%, transparent 100%)",
        opacity: 0.65, filter: "blur(0.5px)",
        animation: "sunPulseMM 6s ease-in-out infinite",
      }} />
      {/* soft background clouds — always present */}
      <BgCloud style={{ top: "18%", left: "60%", opacity: 0.55, animation: "cloudDrift 42s linear infinite" }} size={80} />
      <BgCloud style={{ top: "42%", left: "20%", opacity: 0.45, animation: "cloudDrift 52s linear infinite" }} size={110} />
      <BgCloud style={{ top: "68%", left: "78%", opacity: 0.4, animation: "cloudDrift 60s linear infinite" }} size={90} />

      {/* Rising sparkles from transformation */}
      {flying && (
        <>
          <Sparkle style={{ top: "50%", left: "20%", animationDelay: "0.1s" }} />
          <Sparkle style={{ top: "40%", left: "25%", animationDelay: "0.4s" }} />
          <Sparkle style={{ top: "60%", left: "18%", animationDelay: "0.7s" }} />
          <Sparkle style={{ top: "35%", left: "22%", animationDelay: "1.0s" }} />
        </>
      )}

      {/* Storm clouds passing */}
      {visibleClouds.map((c) => (
        <StormCloud key={c.id} yPct={c.yPct} />
      ))}

      {/* Central multiplier badge */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="text-center">
          <div className={`font-display text-6xl sm:text-7xl transition-colors ${reverted ? "text-red-500" : "text-white/90"}`}
               style={{ textShadow: "0 4px 20px rgba(0,0,0,0.35), 0 0 40px rgba(255,255,255,0.2)" }}>
            {mult.toFixed(2)}×
          </div>
          {flying && !reverted && (
            <div className="mt-2 text-xs font-mono uppercase tracking-widest text-white/60">
              {elapsed.toFixed(1)}s aloft {cloudHits > 0 ? `· −${cloudHits} storm hit${cloudHits === 1 ? "" : "s"}` : ""}
            </div>
          )}
          {reverted && (
            <div className="mt-2 text-sm font-mono uppercase tracking-widest text-red-300 animate-pulse">Spell broken</div>
          )}
        </div>
      </div>

      {/* Rainbow trail streaming behind the flying unicorn — grows with multiplier */}
      {flying && !reverted && (
        <RainbowTrail altitude={altitude} mult={mult} />
      )}

      {/* Unicorn / horse sprite */}
      <div className="absolute" style={{
        left: "12%",
        bottom: `${20 + altitude}%`,
        transform: `translateY(0) ${shake ? "translateX(3px)" : ""}`,
        transition: "bottom 0.28s ease-out",
        animation: flying && !reverted ? "unicornHover 2.2s ease-in-out infinite" : undefined,
      }}>
        <UnicornSprite mode={reverted ? "horse-falling" : (flying ? "unicorn" : "horse")} shake={shake} size={160} />
      </div>

      {/* wind streaks when flying */}
      {flying && !reverted && (
        <>
          <WindStreak style={{ top: "40%", animationDuration: "1.6s" }} />
          <WindStreak style={{ top: "55%", animationDuration: "2.1s", animationDelay: "0.4s" }} />
          <WindStreak style={{ top: "70%", animationDuration: "1.9s", animationDelay: "0.8s" }} />
        </>
      )}

      <style>{`
        @keyframes sunPulseMM { 0%,100% { opacity: 0.55; transform: scale(1); } 50% { opacity: 0.75; transform: scale(1.05); } }
        @keyframes cloudDrift { 0% { transform: translateX(0); } 100% { transform: translateX(-140vw); } }
        @keyframes unicornHover { 0%,100% { transform: translateY(0) rotate(-1deg); } 50% { transform: translateY(-8px) rotate(1deg); } }
        @keyframes stormCloud { 0% { transform: translateX(120%); opacity: 0; } 15% { opacity: 1; } 55% { transform: translateX(-20%); opacity: 1; } 85% { transform: translateX(-160%); opacity: 0.6; } 100% { transform: translateX(-200%); opacity: 0; } }
        @keyframes sparkleRise { 0% { transform: translateY(0) scale(0.5); opacity: 0; } 30% { opacity: 1; } 100% { transform: translateY(-60px) scale(1); opacity: 0; } }
        @keyframes windStreak { 0% { transform: translateX(120%); opacity: 0; } 30% { opacity: 0.6; } 100% { transform: translateX(-40%); opacity: 0; } }
        @keyframes horseFall { 0% { transform: rotate(0) translateY(0); } 100% { transform: rotate(35deg) translateY(80px); opacity: 0.6; } }
        @keyframes boostPulse { 0%,100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(226,185,106,0.5); } 50% { transform: scale(1.05); box-shadow: 0 0 0 8px rgba(226,185,106,0); } }
      `}</style>
    </div>
  );
}

function BgCloud({ style, size = 80 }) {
  return (
    <svg width={size} height={size * 0.55} viewBox="0 0 100 55" className="absolute pointer-events-none" style={style} aria-hidden>
      <path d="M15 40 Q5 40 5 30 Q5 20 18 22 Q20 8 38 12 Q48 4 60 12 Q78 8 82 24 Q95 24 95 36 Q95 46 82 46 L18 46 Q10 46 15 40 Z"
            fill="#F8FAFC" opacity="0.9" />
      <path d="M20 32 Q30 28 40 32 M50 30 Q60 26 72 30" stroke="#E2E8F0" strokeWidth="1" fill="none" opacity="0.6" />
    </svg>
  );
}

function StormCloud({ yPct }) {
  return (
    <div className="absolute pointer-events-none" style={{
      top: `${yPct}%`, left: 0, width: "100%",
      animation: "stormCloud 3.4s ease-in-out forwards",
    }}>
      <svg width="120" height="72" viewBox="0 0 120 72" aria-hidden>
        <defs>
          <radialGradient id="stormFill" cx="50%" cy="40%" r="60%">
            <stop offset="0%" stopColor="#4B5563" />
            <stop offset="100%" stopColor="#1F2937" />
          </radialGradient>
        </defs>
        <path d="M18 48 Q6 48 6 36 Q6 22 22 24 Q24 8 44 12 Q56 2 70 14 Q90 8 96 28 Q112 28 112 42 Q112 54 96 54 L22 54 Q10 54 18 48 Z"
              fill="url(#stormFill)" filter="drop-shadow(0 6px 10px rgba(0,0,0,0.4))" />
        <path d="M60 54 L54 66 L64 60 L58 72" stroke="#FBBF24" strokeWidth="2" fill="none" strokeLinecap="round" />
        <path d="M76 54 L70 62" stroke="#FCD34D" strokeWidth="1.5" fill="none" strokeLinecap="round" opacity="0.7" />
      </svg>
    </div>
  );
}

function Sparkle({ style }) {
  return (
    <div className="absolute pointer-events-none" style={{ ...style, animation: "sparkleRise 2.4s ease-out infinite" }}>
      <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden>
        <path d="M7 0 L8 6 L14 7 L8 8 L7 14 L6 8 L0 7 L6 6 Z" fill="#FEF3C7" />
      </svg>
    </div>
  );
}

function WindStreak({ style }) {
  return (
    <div className="absolute" style={{ ...style, left: 0, width: "100%", height: 2, animation: "windStreak 1.8s linear infinite" }}>
      <div className="mx-auto" style={{ width: "20%", height: 2, background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)" }} />
    </div>
  );
}

/**
 * Rainbow trail streaming from the unicorn's tail across the sky.
 * Positioned to the right of the sprite (which anchors at left:12%) so the trail
 * fills the sky. Length and opacity scale with multiplier so a longer flight
 * grows a more glorious rainbow.
 */
function RainbowTrail({ altitude, mult }) {
  const intensity = Math.min(1, mult / 6);
  const bands = [
    { color: "#F4A5C0", offset: -14 },
    { color: "#F9C89A", offset: -7 },
    { color: "#FEF3C7", offset: 0 },
    { color: "#B5E4D0", offset: 7 },
    { color: "#D9C4E8", offset: 14 },
  ];
  return (
    <div className="absolute pointer-events-none" style={{
      left: "calc(12% + 90px)",
      bottom: `calc(${20 + altitude}% + 40px)`,
      width: "70%",
      height: 40,
      opacity: 0.5 + intensity * 0.5,
      filter: `drop-shadow(0 0 ${6 + intensity * 10}px rgba(244, 165, 192, 0.5))`,
    }}>
      {bands.map((b, i) => (
        <div key={i} className="absolute" style={{
          left: 0, right: 0, top: `calc(50% + ${b.offset}px)`,
          height: 5,
          borderRadius: 4,
          background: `linear-gradient(90deg, transparent 0%, ${b.color}CC 20%, ${b.color} 50%, ${b.color}88 85%, transparent 100%)`,
          transform: "translateY(-50%)",
          animation: `rainbowFlow ${2 + i * 0.15}s linear infinite`,
        }} />
      ))}
      {/* sparkles riding the trail */}
      <TrailSparkle style={{ top: "35%", left: "20%", animationDelay: "0s" }} />
      <TrailSparkle style={{ top: "60%", left: "45%", animationDelay: "0.4s" }} />
      <TrailSparkle style={{ top: "20%", left: "70%", animationDelay: "0.8s" }} />
      <style>{`
        @keyframes rainbowFlow {
          0% { transform: translateY(-50%) translateX(0) scaleX(1); }
          50% { transform: translateY(-50%) translateX(-8px) scaleX(1.02); }
          100% { transform: translateY(-50%) translateX(0) scaleX(1); }
        }
        @keyframes trailSparkleFloat {
          0%,100% { opacity: 0.3; transform: translateY(0) scale(0.7); }
          50% { opacity: 1; transform: translateY(-4px) scale(1); }
        }
      `}</style>
    </div>
  );
}

function TrailSparkle({ style }) {
  return (
    <div className="absolute" style={{ ...style, width: 6, height: 6, animation: "trailSparkleFloat 1.6s ease-in-out infinite" }}>
      <svg viewBox="0 0 6 6" width="100%" height="100%" aria-hidden>
        <path d="M3 0 L3.7 2.3 L6 3 L3.7 3.7 L3 6 L2.3 3.7 L0 3 L2.3 2.3 Z" fill="#FEF3C7" />
      </svg>
    </div>
  );
}

/**
 * Classic unicorn / horse SVG — cleaner silhouette (four visible legs, rounded
 * body, prominent head) with the star of the show: a tall gold spiral horn.
 * When mode=horse: no horn, no sparkles, mane grey. When flying (unicorn):
 * rainbow-tufted mane and tail, gold horn, magical aura and sparkles.
 */
function UnicornSprite({ mode = "unicorn", size = 130, shake = false }) {
  const isUnicorn = mode === "unicorn";
  const isFalling = mode === "horse-falling";
  return (
    <div style={{
      width: size, height: size,
      filter: `drop-shadow(0 12px 18px rgba(0,0,0,0.35)) ${shake ? "hue-rotate(8deg) brightness(0.9)" : ""}`,
      transition: "filter 0.2s",
      animation: isFalling ? "horseFall 1.6s ease-in forwards" : undefined,
    }}>
      {/* Clean side-view unicorn. Head on the RIGHT, tail on the LEFT.
          Every part connects naturally: rear legs under rump, front legs under
          chest, neck arcs up to the head, horn between the ears, mane cascades
          down the neck, tail flows off the rump. */}
      <svg viewBox="0 0 200 180" width="100%" height="100%" aria-hidden>
        <defs>
          <linearGradient id="uBody" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FDF1E6" />
            <stop offset="60%" stopColor="#F5DFCC" />
            <stop offset="100%" stopColor="#E9C2A6" />
          </linearGradient>
          <linearGradient id="uHorn" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FFFBEB" />
            <stop offset="35%" stopColor="#FDE68A" />
            <stop offset="75%" stopColor="#F59E0B" />
            <stop offset="100%" stopColor="#B45309" />
          </linearGradient>
          <linearGradient id="uMane1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#F4A5C0"/><stop offset="100%" stopColor="#E27FA6"/></linearGradient>
          <linearGradient id="uMane2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#F9C89A"/><stop offset="100%" stopColor="#F4A466"/></linearGradient>
          <linearGradient id="uMane3" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#B5E4D0"/><stop offset="100%" stopColor="#7ACBB0"/></linearGradient>
          <linearGradient id="uMane4" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#D9C4E8"/><stop offset="100%" stopColor="#B49BD4"/></linearGradient>
          <linearGradient id="uGrey" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#9CA3AF"/><stop offset="100%" stopColor="#4B5563"/></linearGradient>
          <radialGradient id="uAura" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FEF3C7" stopOpacity="0.9" />
            <stop offset="70%" stopColor="#FBBF24" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#FBBF24" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Aura behind the horn (unicorn only) */}
        {isUnicorn && (
          <circle cx="150" cy="30" r="42" fill="url(#uAura)" style={{ animation: "uAura 3s ease-in-out infinite" }} />
        )}

        {/* --- TAIL (behind body, on the LEFT/rear) --- */}
        <g style={isUnicorn ? { transformOrigin: "40px 100px", animation: "uTail 1.5s ease-in-out infinite" } : undefined}>
          {isUnicorn ? (
            <>
              <path d="M42 100 Q26 100 18 120 Q28 118 34 116 Q22 128 26 148 Q34 138 40 132 Z" fill="url(#uMane1)" />
              <path d="M42 104 Q30 106 22 124 Q30 122 36 120 Q28 138 32 152 Q36 144 40 138 Z" fill="url(#uMane2)" opacity="0.9" />
              <path d="M42 108 Q34 112 30 128 Q36 126 38 124 Q34 142 38 154 Q40 148 42 142 Z" fill="url(#uMane3)" opacity="0.9" />
              <path d="M42 112 Q38 116 36 132 Q40 130 41 128 Q40 148 44 156 Q44 150 45 144 Z" fill="url(#uMane4)" opacity="0.9" />
            </>
          ) : (
            <path d="M42 100 Q22 98 14 126 Q24 122 32 120 Q20 140 26 158 Q34 148 40 140 Q38 148 44 148 Z" fill="url(#uGrey)" />
          )}
        </g>

        {/* --- BACK LEGS (under rump, at x ≈ 55 and 72) --- */}
        <g>
          {/* rear left (further leg, drawn behind body) */}
          <rect x="52" y="108" width="10" height="42" rx="3" fill="url(#uBody)" opacity="0.85" />
          <ellipse cx="57" cy="152" rx="6" ry="2.5" fill="#F4A5A5" />
          {/* rear right (nearer leg) */}
          <rect x="68" y="108" width="10" height="42" rx="3" fill="url(#uBody)" />
          <ellipse cx="73" cy="152" rx="6" ry="2.5" fill="#F4A5A5" />
        </g>

        {/* --- BODY (main torso ellipse, cx=100) --- */}
        <ellipse cx="100" cy="98" rx="52" ry="22" fill="url(#uBody)" />
        <path d="M60 106 Q100 118 140 106" stroke="#D9AF8C" strokeWidth="1.3" fill="none" opacity="0.55" strokeLinecap="round" />
        <ellipse cx="72" cy="90" rx="14" ry="6" fill="#FDF1E6" opacity="0.55" />

        {/* --- FRONT LEGS (under chest, at x ≈ 125 and 142) --- */}
        <g>
          <rect x="122" y="108" width="10" height="42" rx="3" fill="url(#uBody)" opacity="0.85" />
          <ellipse cx="127" cy="152" rx="6" ry="2.5" fill="#F4A5A5" />
          <rect x="138" y="108" width="10" height="42" rx="3" fill="url(#uBody)" />
          <ellipse cx="143" cy="152" rx="6" ry="2.5" fill="#F4A5A5" />
        </g>

        {/* --- NECK (curves up-right from front of body to base of head) --- */}
        <path d="M138 92 Q136 74 148 62 Q152 55 160 55 Q170 55 168 68 Q164 78 158 82 Q152 92 148 100 Z"
              fill="url(#uBody)" />

        {/* --- MANE (starts at the crown and cascades DOWN the back of the neck onto the shoulder) --- */}
        {isUnicorn ? (
          <>
            {/* crown/forelock between ears */}
            <path d="M155 36 Q160 24 168 30 Q170 22 174 32 Q172 44 166 44 Q160 46 155 36 Z" fill="url(#uMane1)" />
            {/* neck mane layer 1 (pink) */}
            <path d="M152 44 Q140 50 134 66 Q142 62 148 60 Q142 76 138 88 Q146 78 152 72 Z" fill="url(#uMane1)" />
            {/* neck mane layer 2 (coral) */}
            <path d="M146 56 Q134 64 128 82 Q138 76 144 74 Q138 92 134 102 Q142 92 148 84 Z" fill="url(#uMane2)" opacity="0.95" />
            {/* neck mane layer 3 (mint) */}
            <path d="M140 72 Q128 80 122 96 Q132 90 138 88 Q132 106 130 116 Q140 106 144 96 Z" fill="url(#uMane3)" opacity="0.95" />
            {/* shoulder tuft (lavender) */}
            <path d="M134 88 Q124 94 122 108 Q130 102 136 100 Q132 114 132 122 Q136 116 140 110 Z" fill="url(#uMane4)" opacity="0.95" />
          </>
        ) : (
          <>
            <path d="M155 36 Q160 24 168 30 Q170 22 174 32 Q172 44 166 44 Q160 46 155 36 Z" fill="url(#uGrey)" />
            <path d="M152 44 Q136 54 128 90 Q140 80 150 78 Q140 104 132 120 Q146 104 152 84 Z" fill="url(#uGrey)" />
          </>
        )}

        {/* --- HEAD (attached to top of neck, cx=163 cy=52) --- */}
        <ellipse cx="163" cy="52" rx="16" ry="14" fill="url(#uBody)" />
        {/* MUZZLE — snout extending forward-right from head */}
        <ellipse cx="176" cy="58" rx="8" ry="6" fill="url(#uBody)" />
        <ellipse cx="178" cy="60" rx="4" ry="3" fill="#F0C9B0" />
        <ellipse cx="180" cy="58" rx="1" ry="1.3" fill="#8B5A3C" />
        <path d="M175 64 Q178 66 175 68" stroke="#8B5A3C" strokeWidth="0.8" fill="none" strokeLinecap="round" />
        {/* CLOSED EYE + lashes (on head, facing right) */}
        <path d="M162 48 Q167 52 172 48" stroke="#1F2937" strokeWidth="1.8" fill="none" strokeLinecap="round" />
        <path d="M162 48 L160 44 M164 49 L163 44 M166 49.5 L166 44 M169 49.5 L169 44 M171 49 L172 44 M172 48 L174 44" stroke="#1F2937" strokeWidth="0.9" strokeLinecap="round" />
        {/* CHEEK BLUSH */}
        <ellipse cx="166" cy="60" rx="4" ry="2.4" fill="#F4A5A5" opacity="0.55" />

        {/* --- EARS (behind horn, on top of head) --- */}
        <path d="M152 38 L154 28 L160 40 Z" fill="url(#uBody)" />
        <path d="M153 38 L155 30 L158 38 Z" fill="#F4C7D9" />
        <path d="M164 36 L168 24 L174 38 Z" fill="url(#uBody)" />
        <path d="M165 36 L168 26 L172 36 Z" fill="#F4C7D9" />

        {/* --- HORN (unicorn only — big golden spiral between ears, aimed straight up) --- */}
        {isUnicorn && (
          <g style={{ transformOrigin: "160px 24px", animation: "uHornGlow 2.6s ease-in-out infinite" }}>
            <ellipse cx="160" cy="24" rx="6" ry="2.5" fill="#92400E" />
            {/* tall tapered horn body */}
            <path d="M154 24 L160 -34 L166 24 Z" fill="url(#uHorn)" stroke="#78350F" strokeWidth="0.8" strokeLinejoin="round" />
            {/* spiral chevron ridges */}
            <path d="M156 16 Q160 13 164 16" stroke="#92400E" strokeWidth="1.1" fill="none" />
            <path d="M156.5 6 Q160 3 163.5 6" stroke="#92400E" strokeWidth="1" fill="none" />
            <path d="M157 -4 Q160 -7 163 -4" stroke="#92400E" strokeWidth="0.9" fill="none" />
            <path d="M157.5 -14 Q160 -16 162.5 -14" stroke="#92400E" strokeWidth="0.8" fill="none" />
            <path d="M158 -24 Q160 -25 162 -24" stroke="#78350F" strokeWidth="0.7" fill="none" />
            {/* central highlight running the length of the horn */}
            <path d="M160 16 L160 -32" stroke="#FEF9E1" strokeWidth="1" fill="none" strokeLinecap="round" opacity="0.85" />
            {/* tip shine */}
            <circle cx="160" cy="-32" r="2" fill="#FEF9E1" style={{ animation: "uHornTip 1.4s ease-in-out infinite" }} />
          </g>
        )}

        {/* Sparkles near the horn (unicorn only) */}
        {isUnicorn && (
          <g>
            <path d="M188 10 L189.5 16 L196 17.5 L189.5 19 L188 25 L186.5 19 L180 17.5 L186.5 16 Z" fill="#FEF3C7" style={{ animation: "uSparkle 2.2s ease-in-out infinite" }} />
            <path d="M132 -2 L133 3 L138 4 L133 5 L132 10 L131 5 L126 4 L131 3 Z" fill="#FEF3C7" opacity="0.9" style={{ animation: "uSparkle 2.4s ease-in-out infinite 0.6s" }} />
            <path d="M182 44 L183 47 L186 48 L183 49 L182 52 L181 49 L178 48 L181 47 Z" fill="#FEF3C7" opacity="0.75" style={{ animation: "uSparkle 2s ease-in-out infinite 1.1s" }} />
          </g>
        )}

        <style>{`
          @keyframes uTail { 0%,100% { transform: rotate(-8deg); } 50% { transform: rotate(10deg); } }
          @keyframes uHornGlow { 0%,100% { filter: drop-shadow(0 0 4px #FDE68A); } 50% { filter: drop-shadow(0 0 14px #FBBF24); } }
          @keyframes uAura { 0%,100% { opacity: 0.6; } 50% { opacity: 1; } }
          @keyframes uHornTip { 0%,100% { opacity: 0.5; r: 1.5; } 50% { opacity: 1; r: 2.4; } }
          @keyframes uSparkle { 0%,100% { opacity: 0.3; transform-origin: center; transform: scale(0.7); } 50% { opacity: 1; transform: scale(1.15); } }
        `}</style>
      </svg>
    </div>
  );
}
